import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import InfoBox from 'components/Shared/InfoBox'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import cn from 'classnames'
import { minTotalBudgetFloor } from 'domain/runtimeBudget/floors'
import { parseLocaleNumber } from 'utils/helpers'
import { useCampaignConfig, useCampaignConfigDispatch } from './CampaignConfigProvider'

export default function BudgetInputBox({ inputClassNames, budgetInputRef }) {
  const { t } = useTranslation()
  const { campaign, task, editMode, form, loading } = useCampaignConfig()
  const { setCampaignForm, hasPendingTask, hasPendingPublishTask } = useCampaignConfigDispatch()
  const hasTask = !!task
  const isDisabled = hasTask && ['PENDING'].includes(task.status)
  const isEditing = editMode && !isDisabled
  const isReadonly = !isEditing && !isDisabled

  const debounceTimeout = useRef(null)
  const lastValue = useRef('')

  const userCurrency = localStorage.getItem('userCurrency') || 'EUR'

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  const locale = localStorage.getItem('locale')
  const numberFormat = new Intl.NumberFormat(locale)
  const { start_date, end_date, total_budget } = form
  const fallbackStartDate = start_date || campaign.start_date
  const minBudget = minTotalBudgetFloor({
    minBudget: campaign.min_budget,
    minDailyBudget: campaign.min_daily_budget,
    platforms: campaign.platforms,
    days: end_date && moment(end_date).diff(moment(fallbackStartDate), 'days') + 1,
  })

  const handleInputChange = (e) => {
    const value = parseLocaleNumber(e.target.value, locale)
    lastValue.current = value
    setCampaignForm({ total_budget: value })
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }
    debounceTimeout.current = setTimeout(() => {
      // Only validate if value is not empty
      if (lastValue.current) {
        const parsedValue = parseFloat(lastValue.current)
        if (parsedValue < minBudget) {
          setCampaignForm({ total_budget: String(minBudget) })
        }
      }
    }, 1500)
  }

  const inputValue = hasPendingTask()
    ? task?.args.budget
    : total_budget
      ? numberFormat.format(total_budget)
      : ''
  const showingLoading = hasPendingPublishTask() || (loading && !inputValue)

  return (
    <div className="d-flex flex-column justify-content-center">
      <h2 className="card-title-default d-inline-block event-header-title">
        {t('Recommendations.campaignBudget.selectLabel')}
      </h2>
      <div className="d-flex gap-2 align-items-center position-relative">
        <InfoBox
          body={isReadonly ? t('Recommendations.campaignConfig.clickToUpdate') : ''}
          tooltipClassNames="cbr-input-info-box"
          withAnchor={false}
          withArrow={false}
          enabled={isReadonly}
        >
          <input
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            disabled={!isEditing || loading}
            className={inputClassNames}
            ref={budgetInputRef}
            min={minBudget}
          />
          <div
            className={cn('cbr-input-loading', {
              'cbr-input-loading--active': showingLoading,
            })}
          >
            <span>...</span>
          </div>
        </InfoBox>
        <div className="currency-label">{campaign.currency ?? userCurrency}</div>
      </div>
    </div>
  )
}

BudgetInputBox.propTypes = {
  inputClassNames: PropTypes.string,
  budgetInputRef: PropTypes.object,
}
